import { ReactElement, useContext, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import { $unleashFlags } from '@nx-frontend/shared/store/unleash-flags';
import { UnleashFeatures } from '@nx-frontend/shared/types';
import { getPaywallActionDiscounts } from './action-discounts';
import { PaywallContext } from './paywall-context';
import { $paywallStore } from './store';

export const PaywallHeader = () => {
  const { mode, title: contextTitle, isAppsumo, isFreePlan } = useContext(PaywallContext);
  const [paywallState, unleashFlags] = useUnit([$paywallStore, $unleashFlags]);

  const t = useTranslations();

  const title = useMemo(() => {
    const action = unleashFlags[UnleashFeatures.SHOW_DISCOUNT_ACTION] ? getPaywallActionDiscounts() : null;

    if (isAppsumo) {
      return (
        <>
          <PaywallHeaderTitleXl title={contextTitle} />
          <PaywallHeaderTitleLg title={t('features.paywall.appsumo_description', { discountPercent: '60' })} />
        </>
      );
    }

    if (mode === 'trialStart') {
      let title: string | ReactElement | undefined = <>{t('features.paywall.title_organization_ready')}</>;
      let description: string | ReactElement | undefined = '';

      if (action && action.headers[mode]) {
        const titleLocKey = action.headers[mode].titleLocKey;
        const descrLocKey = action.headers[mode].descriptionLocKey;
        title = titleLocKey ? t(titleLocKey) : action.headers[mode].title;
        description = descrLocKey ? t(descrLocKey) : action.headers[mode].description;
      }

      return (
        <>
          <PaywallHeaderTitleXl title={title} />
          <PaywallHeaderTitleLg title={description} />
        </>
      );
    }
    if (mode === 'trialEnd') {
      let title: string | ReactElement | undefined = (
        <>
          <p>{t('features.paywall.title_trial_ended')}</p>
          <p>{t('features.paywall.want_to_keep_features')}</p>
        </>
      );
      let description: string | ReactElement | undefined = '';

      if (action && action.headers[mode]) {
        title = action.headers[mode].title;
        description = action.headers[mode].description;
      }

      return (
        <>
          <PaywallHeaderTitleXl title={title} />
          <PaywallHeaderTitleLg title={description} />
        </>
      );
    }
    if (mode === 'upgrade') {
      let title: string | ReactElement | undefined = '';
      let description: string | ReactElement | undefined = '';

      if (action && action.headers[mode]) {
        const titleLocKey = action.headers[mode].titleLocKey;
        const descrLocKey = action.headers[mode].descriptionLocKey;
        title = titleLocKey ? t(titleLocKey) : action.headers[mode].title;
        description = descrLocKey ? t(descrLocKey) : action.headers[mode].description;
      }

      const cnt = paywallState.members;
      return (
        <>
          <PaywallHeaderTitleXl title={title || contextTitle} />
          <PaywallHeaderTitleLg
            title={
              description ||
              (!isFreePlan ? (
                ['plus-yearly', 'plus-monthly'].includes(paywallState.currentPlan?.code || '') ? (
                  <>
                    {t.rich('features.paywall.your_current_plan_with_members', {
                      strong: (chunks) => <span className="font-semibold">{chunks}</span>,
                      plan: paywallState.currentPlan?.name.en,
                      membersAmount: cnt,
                    })}
                  </>
                ) : (
                  <>
                    {t.rich('features.paywall.assemble_toolkit_with_members', {
                      strong: (chunks) => <span className="font-semibold">{chunks}</span>,
                      membersAmount: cnt,
                    })}
                  </>
                )
              ) : (
                <>
                  {t.rich('features.paywall.your_current_plan', {
                    strong: (chunks) => <span className="font-semibold">{chunks}</span>,
                    plan: 'Free',
                  })}
                </>
              ))
            }
          />
        </>
      );
    }
    if (mode === 'addons') {
      return (
        <>
          <PaywallHeaderTitleXl title={contextTitle} />
          <PaywallHeaderTitleLg title={t('features.paywall.want_more_addons')} />
        </>
      );
    }
    return <PaywallHeaderTitleXl title={contextTitle} />;
  }, [contextTitle, isAppsumo, isFreePlan, mode, paywallState.members, paywallState.currentPlan, unleashFlags, t]);

  return <div className="paywall__header text-center mb-[39px]">{title}</div>;
};

const PaywallHeaderTitleXl = (props: { title?: string | ReactElement }) => {
  if (!props.title) return null;

  const className = 'paywall__header-title text-2xl font-semibold text-neutral-800';

  if (typeof props.title === 'string') {
    return (
      <div
        className={className}
        dangerouslySetInnerHTML={{ __html: (props.title as string).replace(/__br__/g, '</br>') }}
      />
    );
  }

  return <div className={className}>{props.title}</div>;
};

const PaywallHeaderTitleLg = (props: { title?: string | ReactElement }) => {
  if (!props.title) return null;
  return <div className="paywall__header-subtitle text-base font-normal text-neutral-800 mt-3">{props.title}</div>;
};
